<template>
  <div>
    <div v-if="loading">Loading editor...</div>
    <div v-else-if="error" class="error">Failed to load editor: {{ error }}</div>
    <editor v-else v-model="content" :init="initOptions" @input="updateContent" />
  </div>
</template>

<script>
import { loadTinyMCE } from '@/helper/tinymce-loader';
import Editor from '@tinymce/tinymce-vue';

export default {
  components: {
    Editor
  },
  props: {
    value: String,
    disabled: Boolean
  },
  data() {
    return {
      content: this.value || '',
      loading: true,
      error: null,
      isEditorDisabled: this.disabled,
      editorInstance: null,
      initOptions: {
        base_url: '/tinymce',
        suffix: '.min',
        skin_url: '/tinymce/skins/ui/oxide',
        content_css: '/tinymce/skins/content/default/content.css',
        height: 500,
        menubar: 'file edit view insert format tools help',
        relative_urls: false,
        remove_script_host: false,
        convert_urls: false,
        plugins: [
          'table',
          'code',
          'help',
          'media',
          'lists',
          'link',
          'image',
          'print',
          'preview',
          'wordcount',
          'insertdatetime',
          'searchreplace',
          'advlist',
          'autolink',
          'charmap',
          'anchor',
          'visualblocks',
          'paste'
        ],
        toolbar:
          'undo redo | fontfamily | blocks | fontsize | bold italic underline forecolor alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image',
        branding: false,
        promotion: false,
        table_toolbar:
          'tableprops tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow | ' +
          'tableinsertcolbefore tableinsertcolafter tabledeletecol',
        table_appearance_options: false,
        table_grid: true,
        table_header_type: 'section',

        setup: editor => {
          this.editorInstance = editor;

          editor.on('change', () => {
            this.$emit('input', editor.getContent());
          });

          this.$nextTick(() => {
            this.updateEditorDisabledState();
          });
        }
      }
    };
  },
  methods: {
    updateContent(newContent) {
      this.content = newContent;
      this.$emit('input', newContent);
    },

    updateEditorDisabledState() {
      if (this.editorInstance && typeof this.editorInstance.mode === 'object') {
        try {
          if (this.isEditorDisabled) {
            this.editorInstance.mode.set('readonly');
          } else {
            this.editorInstance.mode.set('design');
          }
        } catch (error) {
          console.warn('Failed to update editor disabled state:', error);
        }
      }
    }
  },
  async mounted() {
    try {
      await loadTinyMCE();
      this.loading = false;
    } catch (err) {
      this.error = err.message;
      this.loading = false;
    }
  },
  watch: {
    value(newVal) {
      if (newVal !== this.content) {
        this.content = newVal;
      }
    },

    disabled: {
      immediate: true,
      handler(newDisabled) {
        this.isEditorDisabled = newDisabled;
        this.updateEditorDisabledState();
      }
    }
  }
};
</script>

<style>
.tox-editor-header,
.tox-menubar,
.tox-toolbar__primary {
  background: #f5f5f5 !important;
}

.tox-toolbar-overlord {
  padding: 0 !important;
  border-top: 0 !important;
  margin: 0 !important;
}

.tox-tbtn--select {
  background-color: white !important;
}

.error {
  color: red;
  padding: 10px;
  border: 1px solid red;
  background-color: #ffe6e6;
}

/* Style for disabled state */
.tox-tinymce--disabled {
  opacity: 0.7;
}

.tox-tinymce--disabled .tox-toolbar {
  pointer-events: none;
}

.tox-tinymce--disabled .tox-edit-area {
  cursor: not-allowed;
}
</style>
